"use client";

import { useState, type FormEvent } from "react";
import { Sparkles } from "@/components/icons";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { openPublicChat } from "@/lib/chat/open-public-chat";
import { cn } from "@/lib/utils";
import { LandingDemoPlayToggle } from "./landing-demo-play-toggle";
import { LANDING_FIELD_BAR_CLASSES } from "./landing-field-bar";
import { useDemoLoop } from "./use-demo-loop";

const DEMO_EXCHANGES = [
  {
    question: "What's the difference between grade A and grade B pallets?",
    answer: "Grade A pallets have clean, unbroken boards and no repairs. Grade B pallets have been repaired, usually with a replaced board or block, and cost less.",
  },
  {
    question: "How many gaylord boxes fit on a truck?",
    answer: "A 53-foot trailer fits 26 standard 48 x 40 gaylords on the floor, two wide and thirteen deep. Double stacked, that's 52.",
  },
  {
    question: "Can I reuse an IBC tote for food?",
    answer: "Only a food-grade tote that last held food and was washed and certified for it. A tote that held chemicals should never carry food.",
  },
] as const;

const EXCHANGE_MS = 7000;
const ANSWER_DELAY_MS = 1200;

function DemoExchange({ question, answer }: { question: string; answer: string }) {
  return (
    <div className="flex flex-col gap-3">
      <p className="max-w-[85%] animate-rise self-end rounded-2xl rounded-br-md bg-brand px-4 py-2.5 text-[15px] text-brand-foreground motion-reduce:animate-none">
        {question}
      </p>
      <div
        className="flex max-w-[90%] animate-rise gap-3 self-start rounded-2xl rounded-bl-md bg-muted px-4 py-3 motion-reduce:animate-none"
        style={{ animationDelay: `${ANSWER_DELAY_MS}ms` }}
      >
        <Sparkles aria-hidden className="mt-0.5 size-4 shrink-0 text-brand-strong" />
        <p className="text-[15px] leading-[1.55]">{answer}</p>
      </div>
    </div>
  );
}

function AskField() {
  const [question, setQuestion] = useState("");
  const submit = (event: FormEvent) => {
    event.preventDefault();
    if (question.trim()) openPublicChat(question.trim());
  };
  return (
    <form onSubmit={submit} className={LANDING_FIELD_BAR_CLASSES}>
      <Sparkles aria-hidden className="ml-2 size-5 shrink-0 text-brand-strong" />
      <Input
        variant="ghost"
        aria-label="Ask about packaging"
        maxLength={500}
        placeholder="Ask your own question…"
        value={question}
        onChange={(event) => setQuestion(event.target.value)}
        className="flex-1"
      />
      <Button type="submit" variant="primary" disabled={!question.trim()}>
        Ask
      </Button>
    </form>
  );
}

export function LandingToolAsk() {
  const loop = useDemoLoop(DEMO_EXCHANGES.length, EXCHANGE_MS);
  return (
    <div className="flex flex-col gap-5">
      <div className="flex justify-end">
        <LandingDemoPlayToggle isPlaying={loop.isPlaying} onToggle={loop.toggle} />
      </div>
      <div className="grid">
        {DEMO_EXCHANGES.map((exchange, index) => (
          <div
            key={`${exchange.question}-${index === loop.active}`}
            aria-hidden={index !== loop.active}
            className={cn("[grid-area:1/1]", index !== loop.active && "invisible")}
          >
            <DemoExchange question={exchange.question} answer={exchange.answer} />
          </div>
        ))}
      </div>
      <AskField />
    </div>
  );
}
